import api from "../index";

// Types
interface PromotionFilters {
  page?: number;
  perPage?: number;
  searchText?: string;
  status?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

interface PromotionAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  groupBy?: string;
}

interface PromotionDashboardFilters {
  startDate?: string;
  endDate?: string;
  type?: string;
  status?: string;
  hasGamification?: boolean;
}

// Create a promotion
export const createPromotion = async (values: any) => {
  return await api.post("/promotion", values);
};

// Get all promotions
export const getAllPromotions = async () => {
  return await api.get(`/promotions`);
};

// Get all promotions paginated
export const getAllPromotionsPaginated = async (filters: PromotionFilters) => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.perPage) params.append("perPage", filters.perPage.toString());
  if (filters.searchText) params.append("searchText", filters.searchText);
  if (filters.status) params.append("status", filters.status);
  if (filters.type) params.append("type", filters.type);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  return await api.get(`/promotions/paginated?${params.toString()}`);
};

// Get a promotion by id
export const getPromotionById = async (id: string) => {
  return await api.get(`/promotion/${id}`);
};

// Update a promotion by id
export const updatePromotionById = async (id: string, values: any) => {
  return await api.put(`/promotion/${id}`, values);
};

// Delete a promotion by id
export const deletePromotionById = async (id: string) => {
  return await api.delete(`/promotion/${id}`);
};

// Toggle promotion status
export const togglePromotionStatus = async (id: string) => {
  return await api.patch(`/promotion/${id}/toggle-status`);
};

// Duplicate a promotion
export const duplicatePromotion = async (id: string) => {
  return await api.post(`/promotion/${id}/duplicate`);
};

// Get promotion analytics
export const getPromotionAnalytics = async (id: string) => {
  return await api.get(`/promotion/${id}/analytics`);
};

// Get promotion analytics with filters
export const getPromotionAnalyticsFiltered = async (
  id: string,
  filters: PromotionAnalyticsFilters
) => {
  const params = new URLSearchParams();

  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.groupBy) params.append("groupBy", filters.groupBy);

  return await api.get(`/promotion/${id}/analytics?${params.toString()}`);
};

// Get promotion views over time
export const getPromotionViewsOverTime = async (
  id: string,
  filters: PromotionAnalyticsFilters
) => {
  const params = new URLSearchParams();

  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.groupBy) params.append("groupBy", filters.groupBy);

  return await api.get(`/promotion/${id}/analytics/views?${params.toString()}`);
};

// Get promotion conversions over time
export const getPromotionConversionsOverTime = async (
  id: string,
  filters: PromotionAnalyticsFilters
) => {
  const params = new URLSearchParams();

  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);
  if (filters.groupBy) params.append("groupBy", filters.groupBy);

  return await api.get(`/promotion/${id}/analytics/conversions?${params.toString()}`);
};

// Get promotion audience breakdown
export const getPromotionAudienceBreakdown = async (id: string) => {
  return await api.get(`/promotion/${id}/analytics/audience`);
};

// Get promotion device breakdown
export const getPromotionDeviceBreakdown = async (id: string) => {
  return await api.get(`/promotion/${id}/analytics/devices`);
};

// Get promotion gamification stats
export const getPromotionGamificationStats = async (id: string) => {
  return await api.get(`/promotion/${id}/analytics/gamification`);
};

// Get active promotions for user
export const getActivePromotionsForUser = async () => {
  return await api.get(`/promotions/active`);
};

// Track promotion event (view, click, conversion)
export const trackPromotionEvent = async (
  id: string,
  event: "view" | "click" | "dismiss" | "conversion",
  metadata?: any
) => {
  return await api.post(`/promotion/${id}/track`, { event, metadata });
};

// Validate promotion coupon
export const validatePromotionCoupon = async (code: string) => {
  return await api.post(`/promotion/coupon/validate`, { code });
};

// ==================== DASHBOARD ANALYTICS API ====================

// Get promotions dashboard overview stats
export const getPromotionsDashboard = async (filters?: PromotionDashboardFilters) => {
  const params = new URLSearchParams();

  if (filters?.startDate) params.append("startDate", filters.startDate);
  if (filters?.endDate) params.append("endDate", filters.endDate);
  if (filters?.type) params.append("type", filters.type);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.hasGamification !== undefined) {
    params.append("hasGamification", filters.hasGamification.toString());
  }

  const queryString = params.toString();
  return await api.get(`/promotions/dashboard${queryString ? `?${queryString}` : ""}`);
};

// Get detailed promotions stats
export const getPromotionsStats = async (filters?: PromotionDashboardFilters) => {
  const params = new URLSearchParams();

  if (filters?.startDate) params.append("startDate", filters.startDate);
  if (filters?.endDate) params.append("endDate", filters.endDate);
  if (filters?.type) params.append("type", filters.type);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.hasGamification !== undefined) {
    params.append("hasGamification", filters.hasGamification.toString());
  }

  const queryString = params.toString();
  return await api.get(`/promotions/stats${queryString ? `?${queryString}` : ""}`);
};

// Get top performing promotions
export const getTopPerformingPromotions = async (
  limit?: number,
  sortBy?: "views" | "clicks" | "conversions" | "revenue"
) => {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (sortBy) params.append("sortBy", sortBy);

  const queryString = params.toString();
  return await api.get(`/promotions/top-performing${queryString ? `?${queryString}` : ""}`);
};

// Get promotions performance over time
export const getPromotionsPerformanceOverTime = async (
  filters?: PromotionDashboardFilters & { groupBy?: string }
) => {
  const params = new URLSearchParams();

  if (filters?.startDate) params.append("startDate", filters.startDate);
  if (filters?.endDate) params.append("endDate", filters.endDate);
  if (filters?.groupBy) params.append("groupBy", filters.groupBy);
  if (filters?.type) params.append("type", filters.type);

  const queryString = params.toString();
  return await api.get(`/promotions/performance-over-time${queryString ? `?${queryString}` : ""}`);
};

// Export promotions analytics data
export const exportPromotionsAnalytics = async (
  format: "csv" | "json",
  filters?: PromotionDashboardFilters
) => {
  const params = new URLSearchParams();
  params.append("format", format);

  if (filters?.startDate) params.append("startDate", filters.startDate);
  if (filters?.endDate) params.append("endDate", filters.endDate);
  if (filters?.type) params.append("type", filters.type);
  if (filters?.status) params.append("status", filters.status);

  return await api.get(`/promotions/export?${params.toString()}`, {
    responseType: format === "csv" ? "blob" : "json",
  });
};
